import { putLanguage } from "@Api/languagePut";
import { getTokenManager } from "@Auth/session";
import { useUserContext } from "@Hooks/contexts/useUserContext";
import { AVAILABLE_LANGUAGES, FALLBACK_LANGUAGE, LanguageNativeName } from "@juristic/types/generic/language";
import { styled } from "@mui/material/styles";
import { Avatar, ChevronDownIcon, IconButton, LogoutIcon, Meta, MenuItem, MenuLabel, MenuSeparator, Popover, Tag } from "@UI";
import { setUserLanguage } from "@Utils/setUserLanguage";
import { changeLanguage } from "i18next";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
const COLLAPSED_LANGUAGE_COUNT = 6;
const AccountButton = styled(IconButton)({
    borderRadius: "50%"
});
const Identity = styled("div")({
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: "var(--j-sp-1-5) var(--j-sp-2) var(--j-sp-2)",
    "& > .identity-text": {
        minWidth: 0
    },
    "& .identity-name": {
        fontWeight: 600,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },
    "& .identity-email": {
        display: "block",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    }
});
const LanguageCode = styled(Tag)({
    width: "1.75rem",
    justifyContent: "center",
    padding: 0,
    fontSize: "var(--j-fs-2xs)",
    fontWeight: 600,
    "[aria-checked=true] > &": {
        background: "var(--j-surface)",
        color: "var(--j-accent-ink)"
    }
});
const languageCode = (language)=>(language.split("-").pop() ?? language).toUpperCase();
export function AccountMenu() {
    const { t } = useTranslation();
    const anchorRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [showAllLanguages, setShowAllLanguages] = useState(false);
    const [revealedLanguage, setRevealedLanguage] = useState(null);
    const languageGroupRef = useRef(null);
    const languageLabelId = useId();
    const setUser = useUserContext((s)=>s.setUser);
    const user = useUserContext((s)=>s?.user?.meta?.dbUser);
    const token = useUserContext((s)=>s?.user?.meta?.access_token.token);
    const currentLanguage = user?.language || FALLBACK_LANGUAGE;
    const initials = user?.initials || `${user?.first_name?.[0] ?? ""}${user?.last_name?.[0] ?? ""}`.toUpperCase();
    const close = useCallback(()=>{
        setIsOpen(false);
        setShowAllLanguages(false);
    }, []);
    const handleChangeLanguage = useCallback((lang)=>()=>{
            if (!token) {
                close();
                return;
            }
            putLanguage(token, lang).then((l)=>{
                changeLanguage(l);
                setUser(setUserLanguage(l));
            }).finally(()=>{
                close();
            });
        }, [
        token,
        close
    ]);
    const handleLogout = useCallback(async ()=>{
        await getTokenManager().signOut();
        setUser(null);
        localStorage.clear();
    }, []);
    const collapsed = AVAILABLE_LANGUAGES.slice(0, COLLAPSED_LANGUAGE_COUNT);
    const visibleLanguages = showAllLanguages || AVAILABLE_LANGUAGES.length <= COLLAPSED_LANGUAGE_COUNT ? AVAILABLE_LANGUAGES : collapsed.includes(currentLanguage) ? collapsed : [
        ...collapsed,
        currentLanguage
    ];
    const hiddenCount = AVAILABLE_LANGUAGES.length - visibleLanguages.length;
    useEffect(()=>{
        if (!revealedLanguage) return;
        languageGroupRef.current?.querySelector(`[lang="${revealedLanguage}"]`)?.closest("button")?.focus();
        setRevealedLanguage(null);
    }, [
        revealedLanguage
    ]);
    const showMoreLanguages = ()=>{
        setRevealedLanguage(AVAILABLE_LANGUAGES.find((lang)=>!visibleLanguages.includes(lang)) ?? null);
        setShowAllLanguages(true);
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(AccountButton, {
        size: "sm",
        ref: anchorRef,
        "aria-label": t("shell.account"),
        "aria-haspopup": "dialog",
        "aria-expanded": isOpen,
        title: user?.fullName,
        onClick: ()=>isOpen ? close() : setIsOpen(true)
    }, /*#__PURE__*/ React.createElement(Avatar, {
        initials: initials
    })), /*#__PURE__*/ React.createElement(Popover, {
        open: isOpen,
        anchorEl: anchorRef.current,
        onClose: close,
        role: "dialog",
        "aria-label": t("shell.account")
    }, /*#__PURE__*/ React.createElement(Identity, null, /*#__PURE__*/ React.createElement(Avatar, {
        initials: initials,
        size: 32
    }), /*#__PURE__*/ React.createElement("div", {
        className: "identity-text"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "identity-name"
    }, user?.fullName), /*#__PURE__*/ React.createElement(Meta, {
        className: "identity-email"
    }, user?.email))), /*#__PURE__*/ React.createElement(MenuSeparator, null), /*#__PURE__*/ React.createElement("div", {
        role: "menu",
        "aria-label": t("shell.account")
    }, /*#__PURE__*/ React.createElement(MenuLabel, {
        id: languageLabelId
    }, t("language")), /*#__PURE__*/ React.createElement("div", {
        role: "group",
        "aria-labelledby": languageLabelId,
        ref: languageGroupRef
    }, visibleLanguages.map((lang)=>/*#__PURE__*/ React.createElement(MenuItem, {
            key: lang,
            selected: lang === currentLanguage,
            onClick: handleChangeLanguage(lang),
            icon: /*#__PURE__*/ React.createElement(LanguageCode, {
                tone: "grey"
            }, languageCode(lang))
        }, /*#__PURE__*/ React.createElement("span", {
            lang: lang
        }, LanguageNativeName[lang]))), hiddenCount > 0 && /*#__PURE__*/ React.createElement(MenuItem, {
        tone: "muted",
        icon: /*#__PURE__*/ React.createElement(ChevronDownIcon, {
            size: 14
        }),
        onClick: showMoreLanguages
    }, t("shell.moreLanguages", {
        count: hiddenCount
    }))), /*#__PURE__*/ React.createElement(MenuSeparator, null), /*#__PURE__*/ React.createElement(MenuItem, {
        tone: "danger",
        icon: /*#__PURE__*/ React.createElement(LogoutIcon, null),
        onClick: handleLogout
    }, t("logout")))));
}
